import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import axios from 'axios';
import LoadingButton from '../../../components/LoadingButton';
import MultiCollaboratorSelect, { Collaborator } from '../../../components/MultiCollaboratorSelect';
import { hostname, parseError } from '../../../utils';

interface ContentAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  content: any;
  companyId: string;
  getToken: () => Promise<string>;
  onSuccess?: () => void;
  eventListener: (event: any) => void;
}

const ContentAssignmentDialog: React.FC<ContentAssignmentDialogProps> = ({
  open,
  onClose,
  content,
  companyId,
  getToken,
  onSuccess,
  eventListener,
}) => {
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedCollaborator, setSelectedCollaborator] = useState<Collaborator | undefined>();
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Pre-populate if content is already assigned
      if (content?.assignedTo) {
        setSelectedEmail(content.assignedTo.email);
        setNotes(content.assignedTo.notes || '');
      } else {
        setSelectedEmail('');
        setNotes('');
      }
    }
  }, [open, content]);

  const handleAssign = async () => {
    if (!selectedEmail) {
      eventListener({
        type: 'SET_ERROR',
        error: 'Please select a collaborator',
      });
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/${content.id}/assign`,
        {
          companyId,
          assigneeEmail: selectedEmail,
          notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      eventListener({
        type: 'SET_SUCCESS',
        message: 'Content assigned successfully',
      });
      
      onSuccess?.();
      onClose();
    } catch (error) {
      eventListener({
        type: 'SET_ERROR',
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Assign Content for Review</Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {content && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                <strong>Content:</strong> {content.title}
              </Typography>
              <Typography variant="caption">
                Type: {content.type} | Status: {content.status}
              </Typography>
            </Alert>
          )}

          {content?.assignedTo && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Currently assigned to: <strong>{content.assignedTo.email}</strong>
              <br />
              Status: <strong>{content.assignedTo.status}</strong>
            </Alert>
          )}

          <MultiCollaboratorSelect
            multiple={false}
            companyId={companyId}
            getToken={getToken}
            value={selectedEmail}
            onChange={(email, collaborator) => {
              setSelectedEmail(email);
              setSelectedCollaborator(collaborator);
            }}
            onError={(error) => {
              eventListener({
                type: 'SET_ERROR',
                error: error,
              });
            }}
            label="Select Collaborator"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Assignment Notes (Optional)"
            placeholder="Provide instructions or notes for the reviewer..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedEmail}
        >
          Assign Content
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContentAssignmentDialog;